import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, InputAdornment, Radio, RadioGroup, TextField, Typography
} from '@mui/material';
import api from '../../config/axiosConfig';

const STATUS_CHIP = {
  PENDING: { label: 'Pendiente', bgcolor: '#fff3e0', color: '#e65100' },
  NO_IMPACT: { label: 'Sin impacto en costo', bgcolor: '#e8f5e9', color: '#2e7d32' },
  ADJUSTED: { label: 'Ajuste de costo', bgcolor: '#e3f2fd', color: '#1565c0' },
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '');

/**
 * Correcciones en sitio de una cotización (hechas desde "Editar PIPC" con la OT abierta) y su
 * revisión de costo. Ventas/Administración resuelve cada una: sin impacto o ajuste de costo.
 */
const CostReviewDialog = ({ open, quotation, canResolve, onClose, onResolved }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forms, setForms] = useState({});
  const [savingId, setSavingId] = useState(null);

  const loadReviews = async () => {
    if (!quotation?.id) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/quotations/${quotation.id}/cost-reviews`);
      setReviews(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError('No se pudieron cargar las correcciones de esta cotización.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setForms({});
      loadReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quotation?.id]);

  const updateForm = (id, field, value) => setForms(prev => ({ ...prev, [id]: { resolution: 'NO_IMPACT', ...prev[id], [field]: value } }));

  const handleResolve = async (review) => {
    const form = { resolution: 'NO_IMPACT', ...forms[review.id] };
    setSavingId(review.id);
    setError('');
    try {
      await api.post(`/quotations/cost-reviews/${review.id}/resolve`, form);
      await loadReviews();
      if (onResolved) onResolved();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo resolver la revisión de costo.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        💲 Revisión de costo — {quotation?.invoiceFolio || `COT-${quotation?.id}`}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Operaciones corrigió en sitio datos con los que se cotizó. El precio no cambia solo: decide si la corrección
          impacta el costo. El precio aceptado por el cliente no se modifica; un ajuste queda registrado para facturación.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box>
        ) : reviews.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Esta cotización no tiene correcciones en sitio.</Typography>
        ) : (
          reviews.map((review) => {
            const chip = STATUS_CHIP[review.status] || STATUS_CHIP.PENDING;
            const form = { resolution: 'NO_IMPACT', ...forms[review.id] };
            const pending = review.status === 'PENDING';
            const canSubmit = form.resolution === 'NO_IMPACT'
              || (form.amount && Number(form.amount) !== 0 && form.concept?.trim());
            return (
              <Box key={review.id} sx={{ mb: 2, p: 2, borderRadius: 2, border: `1px solid ${pending ? '#ffcc80' : '#e0e0e0'}`, bgcolor: pending ? '#fffbf2' : '#fafafa' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1 }}>{review.entityName}</Typography>
                  <Chip size="small" label={chip.label} sx={{ bgcolor: chip.bgcolor, color: chip.color, fontWeight: 700 }} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{review.changes}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>Motivo: {review.reason}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Corrigió {review.correctedBy} · {formatDateTime(review.createdAt)}
                </Typography>

                {!pending && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {review.status === 'ADJUSTED'
                      ? <>Ajuste: <strong>${Number(review.adjustmentAmount).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong> ({review.adjustmentConcept})</>
                      : 'Sin impacto en costo'}
                    {review.resolutionComment ? ` · ${review.resolutionComment}` : ''}
                    <Typography component="span" variant="caption" color="text.secondary"> — {review.resolvedBy}, {formatDateTime(review.resolvedAt)}</Typography>
                  </Typography>
                )}

                {pending && canResolve && (
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #ffcc80' }}>
                    <RadioGroup row value={form.resolution} onChange={(e) => updateForm(review.id, 'resolution', e.target.value)}>
                      <FormControlLabel value="NO_IMPACT" control={<Radio size="small" />} label="Sin impacto en costo" />
                      <FormControlLabel value="ADJUSTED" control={<Radio size="small" />} label="Ajuste de costo" />
                    </RadioGroup>
                    {form.resolution === 'ADJUSTED' && (
                      <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                        <TextField size="small" type="number" label="Monto del ajuste" required sx={{ width: 180 }}
                          value={form.amount || ''} onChange={(e) => updateForm(review.id, 'amount', e.target.value)}
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                        <TextField size="small" label="Concepto" required sx={{ flex: 1, minWidth: 220 }}
                          placeholder="Ej: Diferencia por 30 empleados adicionales"
                          value={form.concept || ''} onChange={(e) => updateForm(review.id, 'concept', e.target.value)}
                          inputProps={{ maxLength: 255 }} />
                      </Box>
                    )}
                    <TextField size="small" fullWidth label="Comentario (opcional)" sx={{ mt: 1.5 }}
                      value={form.comment || ''} onChange={(e) => updateForm(review.id, 'comment', e.target.value)} />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                      <Button variant="contained" size="small" disabled={!canSubmit || savingId === review.id}
                        startIcon={savingId === review.id ? <CircularProgress size={14} color="inherit" /> : null}
                        onClick={() => handleResolve(review)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                        Resolver
                      </Button>
                    </Box>
                  </Box>
                )}
                {pending && !canResolve && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Pendiente de que Ventas o un Administrador la resuelvan.
                  </Typography>
                )}
              </Box>
            );
          })
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CostReviewDialog;
